import {
  Fragment,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  ICommandBarItemProps,
  IColumn as IOfficeColumn,
  PrimaryButton,
  Stack,
} from "@fluentui/react";
import { Separator } from "cayo.ui";
import styled from "styled-components";
import { AppContext } from "@cayo/shared/components/App/appContext";
import { useSchemes } from "@cayo/shared/components/App/hooks";
import { appSignals } from "@cayo/shared/components/App/utils";
import { assignQuery, newQuery } from "@cayo/shared/components/GridContainer/gridModel/Query";
import { GridControllerAction, IGridState } from "@cayo/shared/components/GridContainer/types";
import logger from "@cayo/shared/libs/logger";
import { commonMessages } from "@cayo/shared/messages/common";
import useService from "@cayo/shared/services/hooks/useService";
import { ProductContext, SubscriptionEnum } from "../../contexts/product-context";
import { getAppId } from "../../utils/app-ids";
import { getLazyProps } from "../../utils/lazy-props";
import { regexs } from "../../utils/regexs";
import CommandBar from "../CommandBar";
import { useToggleNavCollapsed } from "../Nav/nav-hooks";
import SQPicker from "../ObjectPicker/SQPicker";
import QueryFilterForm from "../QueryFilterForm";
import { ChooseColumnsDialog } from "./ChooseColumnsDialog";
import { ColumnData } from "./ChooseColumnsDialog.controller";
import { createController } from "./controller-factory";
import { GridContainerProps, GridController } from "./grid-model/GridController";
import { GridDetails } from "./GridDetails";
import { GridFilterBar } from "./GridFilterBar";
import { useGridExport } from "./hooks/useGridExport";
import { ModernGridFilterBar } from "./ModernGridFilterBar";

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const mainGridId = "ctx_grid_grid";
const log = logger.getLogger("QUICK-FILTER-NEW GridContainer");

export function GridContainer(props: GridContainerProps): JSX.Element {
  const uiInteraction = useService("uiInteraction");
  const location = useLocation();
  const { showPropertiesPanel } = useSchemes();
  const query = useQuery();
  const product = useContext(ProductContext);

  const { manifest } = useContext(AppContext);
  const appId = getAppId(manifest);

  const [abortControllerState, setAbortControllerState] = useState<AbortController | null>(null);
  const previewQueryId = query.get("previewQueryId");
  const { gridSettingsKey } = props;

  const isFree = product?.subscriptionType === SubscriptionEnum.free;
  const restrictedFilter = isFree && props?.scheme?.targetType === "cayo.graph.historyRecord";
  const Wrapper = props.isMainView ? GridBodyWrapper : Fragment;

  const {
    detailsList,
    commandBar,
    filterBar,
    targetType,
    savedQueries,
    isSearchSupported,
    entitySetPath,
    advancedFilterBar,
  } = props.scheme;

  const readonlyQueries = !!savedQueries?.readOnly;
  const commandBarRef = useRef<{ itemsRef: RefObject<ICommandBarItemProps[]> }>(null);

  const groupConfig = useMemo(
    () =>
      detailsList?.columns
        ?.filter((c) => !!c.groupByTitle)
        .map((c) => ({ key: c.key, text: c.groupByTitle! })),
    [detailsList?.columns]
  );

  // TODO: move to controller
  const allColumnsData = useRef(
    detailsList?.columns?.map((c) => ({ fieldName: c.fieldName!, displayName: c.displayName! })) ??
      []
  ).current;

  const [ctrl] = useState<GridController>(() => createController(props, uiInteraction, appId));
  const [gridState, setGridState] = useState<IGridState>(ctrl.state);
  const dispatch = useCallback((action: GridControllerAction) => ctrl.dispatch(action), [ctrl]);
  const [dialogConfig, setDialogConfig] = useState<{
    previewQueryId: string | undefined;
    hidden: boolean;
  }>({
    previewQueryId: undefined,
    hidden: true,
  });
  const queryUrl = props.scheme?.savedQueries?.picker?.path;

  //handle open saved query list
  const { collapsed, toggleCollapsed } = useToggleNavCollapsed();
  const collapsedOnQueryPicker = useRef(false);

  useLayoutEffect(function mount() {
    log.debug(".mount -> gridSettingsKey,  props", gridSettingsKey, props);
    const unsubscribe = ctrl.subscribe((gs) => {
      log.debug("onStateChanged -> GridState", gs);
      setGridState(gs!);
    });

    ctrl.initialize().then(() => {
      previewQueryId &&
        dispatch({ kind: "selectQuery", previewQuery: true, queryId: previewQueryId || "" });
    });

    const actionUrl = getLazyProps(location.pathname); // TODO: invent something more clever
    if (actionUrl) {
      showPropertiesPanel({ id: actionUrl, parentId: detailsList!.id });
    }

    return () => {
      unsubscribe();
      ctrl.dispose();
      if (collapsedOnQueryPicker.current) {
        toggleCollapsed();
      }
      log.debug(".unmount");
    };
  }, []);

  useEffect(() => {
    appSignals.subscribe("queryIsRunning", (data: any) => {
      setAbortControllerState(data?.controller || null);
    });

    appSignals.subscribe("queryIsEnd", () => {
      setAbortControllerState(null);
    });
  }, []);

  //TODO:move to controller
  useEffect(function handleShowSavedQueryFormSignal() {
    return appSignals.subscribe("showSavedQueryForm", (data: any) => {
      const { parameters } = data;
      log.debug("Signal.showSavedQueryForm -> parameters", parameters);
      log.debug(
        "Signal.showSavedQueryForm -> parameters.path, path",
        parameters.path,
        `${savedQueries?.picker?.path}/${parameters.id}`
      );
      dispatch({
        kind: "openEditForm",
        query: assignQuery(newQuery(), { id: parameters.id }),
      });
    });
  }, []);

  //#region collapse left navigation tree

  useEffect(
    function handleShowQueryPicker() {
      if (detailsList?.id !== mainGridId) {
        return;
      }
      if (gridState.showQueryPicker && !collapsed) {
        collapsedOnQueryPicker.current = true;
        toggleCollapsed();
      } else if (collapsedOnQueryPicker.current && !gridState.showQueryPicker && collapsed) {
        toggleCollapsed();
        collapsedOnQueryPicker.current = false;
      }
    },
    [gridState.showQueryPicker]
  );
  //#endregion

  const updateSQ = useCallback((data: { name: string; queryId: string }) => {
    dispatch({
      kind: "updateSQ",
      data: data,
    });
  }, []);

  const { handleExport } = useGridExport({
    detailsList,
    gridState,
    schemeName: props.scheme.name,
  });

  const newQueryPicker = true;
  const showSearchButton = appId === "cayo_administrator"; // TODO: configure using ui schema

  return (
    <Stack
      styles={{
        root: {
          height: "100%",
          position: "relative",
        },
      }}
    >
      {commandBar && (
        <CommandBar
          scheme={commandBar!}
          gridState={gridState}
          dispatch={dispatch}
          uiInteraction={uiInteraction}
          groupConfig={groupConfig}
          showFilterBarItems={!filterBar && !!isSearchSupported && !advancedFilterBar}
          previewQueryId={previewQueryId}
          maxResults={detailsList?.maxResults}
          showSearchButton={showSearchButton}
          isMainView={props.isMainView}
          ref={commandBarRef}
        />
      )}
      <Wrapper>
        {filterBar ? (
          <GridFilterBar
            restrictedFilter={restrictedFilter}
            schema={filterBar}
            columns={detailsList?.columns}
            query={gridState.query}
            propertyAnnotations={props.propertyAnnotations}
            dispatch={dispatch}
            savedQueries={gridState.savedQueries}
            recentQueries={gridState.recentQueries}
            favoriteQueries={gridState.favoriteQueries}
            groupConfig={groupConfig}
            targetType={props.scheme.targetType}
            isSearchSupported={!!isSearchSupported}
            readonly={readonlyQueries}
            gridSettingsKey={props.gridSettingsKey}
            queryMode={gridState.queryMode}
            queryUrl={queryUrl!}
            previewQueryId={previewQueryId}
            selectedItems={gridState.selectedItems}
            entitySetPath={entitySetPath!}
            setAbortControllerState={setAbortControllerState}
            showSearchButton={showSearchButton}
          />
        ) : props?.scheme?.detailsList?.compact ? (
          <Fragment />
        ) : (
          <Separator />
        )}
        {advancedFilterBar && (
          <ModernGridFilterBar
            scheme={advancedFilterBar}
            dispatch={dispatch}
            query={gridState.query}
            showSearchButton={showSearchButton}
            hideSearchText={props.scheme.isSearchSupported === false}
            onExport={handleExport}
            exportConfig={detailsList?.exportConfig}
            exportDisabled={Boolean(!gridState.searchResult.items.length || gridState.loading)}
          />
        )}
        <GridDetails
          query={gridState.query}
          columns={gridState.columns}
          ajustColumnsToken={gridState.ajustColumnsToken}
          items={gridState.searchResult.items}
          groups={gridState.searchResult.groups}
          itemMenu={gridState.itemMenu}
          skipToken={gridState.searchResult.skipToken}
          propertyAnnotations={props.propertyAnnotations}
          isLoading={gridState.loading}
          dispatch={dispatch}
          gridSettingsKey={gridSettingsKey}
          schema={detailsList!}
          selectedItems={gridState.selectedItems}
          previewQueryId={previewQueryId}
          onItemClick={props.onItemClick}
          abortControllerState={abortControllerState}
          setAbortControllerState={setAbortControllerState}
          commandBarRef={commandBarRef}
        />
      </Wrapper>
      {/* query picker */}

      {gridState.showQueryPicker && newQueryPicker && (
        <SQPicker
          isPreviewImmediate={!props.scheme.filterBar?.previewQueryOnDemand}
          onClose={() => dispatch({ kind: "closeQueryPicker" })}
          filter={`targetType eq '${targetType}'`}
          dispatch={dispatch}
          setDialogConfig={setDialogConfig}
          previewQueryId={previewQueryId}
          setAbortControllerState={setAbortControllerState}
        ></SQPicker>
      )}
      {/* /query picker */}
      {/* edit query form */}
      {gridState.showEditForm && (
        <QueryFilterForm
          items={filterBar?.annotations}
          url={savedQueries?.picker?.path}
          targetType={targetType}
          gridId={detailsList?.id}
          readonly={readonlyQueries}
          propertyAnnotations={props.propertyAnnotations}
          {...(gridState.showEditForm?.prms || {})}
          setDialogConfig={setDialogConfig}
          updateSQ={updateSQ}
          onClose={() => {
            log.debug("QueryFilterForm.onClose");
            dispatch({ kind: "closeEditForm" });
          }}
        />
      )}
      {gridState.showChooseColumns && (
        <ChooseColumnsDialog
          onClose={() => {
            log.debug("ChooseColumnsDialog.close");
            dispatch({ kind: "closeChooseColumnsDialog" });
          }}
          onSave={(columns: string[]) => {
            log.debug("ChooseColumnsDialog.save -> columns", columns);
            dispatch({ kind: "applyColumns", columns });
          }}
          allColums={allColumnsData}
          userColumns={gridState.columns.map(toColumnData)}
        />
      )}
      <Dialog
        hidden={dialogConfig.hidden}
        minWidth={424}
        dialogContentProps={{
          showCloseButton: false,
          styles: {
            innerContent: {
              alignItems: "center",
              display: "flex",
              minHeight: "50px",
            },
            subText: { fontSize: 16, fontWeight: 400 },
            title: { fontWeight: 600 },
          },
        }}
        modalProps={{
          isBlocking: true,
          topOffsetFixed: true,
          styles: {
            main: { maxWidth: 450 },
            scrollableContent: { height: "100%" },
          },
        }}
      >
        <div>
          {commonMessages.authPrompts.defaultMessage}{" "}
          <a
            href="https://support.cayosoft.com/hc/en-us/articles/27761136796813"
            rel="noreferrer"
            target="_blank"
          >
            Learn more.
          </a>
        </div>
        <DialogFooter>
          <PrimaryButton
            onClick={() => {
              let currentUrl = window.location.href;
              if (!currentUrl.includes("previewQueryId")) {
                currentUrl += `?previewQueryId=${dialogConfig.previewQueryId}`;
              } else {
                currentUrl = currentUrl.replace(
                  regexs.previewQueryIdRegex,
                  `$1${dialogConfig.previewQueryId}`
                );
              }
              window.open(currentUrl, "_blank");
              setDialogConfig({
                hidden: true,
                previewQueryId: undefined,
              });
            }}
            text={commonMessages.yes.defaultMessage}
          />
          <DefaultButton
            onClick={() =>
              setDialogConfig({
                hidden: true,
                previewQueryId: undefined,
              })
            }
            text={commonMessages.no.defaultMessage}
          />
        </DialogFooter>
      </Dialog>
    </Stack>
  );
}

//TODO: move to controller
function toColumnData(column: IOfficeColumn): ColumnData {
  return { fieldName: column.fieldName!, displayName: column.name };
}

const GridBodyWrapper = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: white;
  background: ${(props) => props.theme.cayoTheme.brandColors.primaryBackground};
  ${(props) => props.theme.elementStyles.gridBodyWrapper}
`;
